import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import InvestmentParametersSection from './InvestmentParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import ContributionsSection from './ContributionsSection';
import TaxAndInflationSection from './TaxAndInflationSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета сложных процентов
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  investmentData,
  setInvestmentData,
  contributionFrequencies,
  compoundingFrequencies,
  saveInvestment,
  investmentTypes
}) => {
  
  // Состояние для имени сохраняемой инвестиции
  const [saveInvestmentName, setSaveInvestmentName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обрабатываем зависимые поля
      if (name === 'includeAdditionalContributions' && !checked) {
        newState.additionalContribution = '0';
        newState.contributionFrequency = 'none';
      }
      
      if (name === 'adjustForInflation' && !checked) {
        newState.inflation = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      initialInvestment, investmentTerm, interestRate, 
      additionalContribution, inflation, taxRate,
      includeAdditionalContributions
    } = formData;

    // Парсинг числовых значений
    const numInitialInvestment = parseFloatOrDefault(initialInvestment, -1);
    const numInvestmentTerm = parseFloatOrDefault(investmentTerm, -1);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);
    const numInflation = parseFloatOrDefault(inflation, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);

    // Валидация базовых полей
    if (numInitialInvestment < 0) newErrors.initialInvestment = 'Сумма должна быть положительной.';
    if (!newErrors.initialInvestment && numInitialInvestment > 100000000) newErrors.initialInvestment = 'Максимальная сумма 100 млн ₽.';
    
    if (numInvestmentTerm <= 0) newErrors.investmentTerm = 'Срок должен быть положительным.';
    if (!newErrors.investmentTerm && numInvestmentTerm > 100) newErrors.investmentTerm = 'Максимальный срок 100 лет.';
    
    if (numInterestRate < 0) newErrors.interestRate = 'Ставка не может быть отрицательной.';
    if (!newErrors.interestRate && numInterestRate > 100) newErrors.interestRate = 'Ставка не может быть более 100%.';

    // Валидация дополнительных параметров
    if (includeAdditionalContributions) {
      const numAdditionalContribution = parseFloatOrDefault(additionalContribution, -1);
      if (numAdditionalContribution < 0) newErrors.additionalContribution = 'Сумма должна быть положительной.';
      if (!newErrors.additionalContribution && numAdditionalContribution > 10000000) newErrors.additionalContribution = 'Максимальная сумма пополнения 10 млн ₽.';
    }

    // Валидация инфляции и налогов
    if (numInflation < 0) newErrors.inflation = 'Инфляция не может быть отрицательной.';
    if (!newErrors.inflation && numInflation > 100) newErrors.inflation = 'Инфляция не может быть более 100%.';
    
    if (numTaxRate < 0) newErrors.taxRate = 'Ставка налога не может быть отрицательной.';
    if (!newErrors.taxRate && numTaxRate > 99) newErrors.taxRate = 'Ставка налога не может быть более 99%.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет сложных процентов
  const calculateCompoundInterest = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setInvestmentData([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Подготовка входных данных
        const initialInvestment = parseFloatOrDefault(formData.initialInvestment);
        const additionalContribution = formData.includeAdditionalContributions 
          ? parseFloatOrDefault(formData.additionalContribution, 0) 
          : 0;
        const annualInterestRate = parseFloatOrDefault(formData.interestRate) / 100;
        const investmentYears = parseFloatOrDefault(formData.investmentTerm);
        const inflationRate = formData.adjustForInflation 
          ? parseFloatOrDefault(formData.inflation, 0) / 100 
          : 0;
        const taxRate = parseFloatOrDefault(formData.taxRate, 0) / 100;
        
        const startDate = new Date(formData.investmentStartDate);
        const contributionFrequency = formData.contributionFrequency;
        const compoundingFrequency = formData.compoundingFrequency;
        
        // Определение количества периодов в году
        const getPeriodsPerYear = (frequency) => {
          switch (frequency) {
            case 'daily': return 365;
            case 'weekly': return 52;
            case 'monthly': return 12;
            case 'quarterly': return 4;
            case 'semi_annually': return 2;
            case 'annually': return 1;
            case 'none': return 0;
            default: return 1;
          }
        };
        
        const compoundingPeriodsPerYear = getPeriodsPerYear(compoundingFrequency);
        const contributionPeriodsPerYear = getPeriodsPerYear(contributionFrequency);
        
        // Расчет нормализованных ставок для различных периодов
        const compoundingRatePerPeriod = annualInterestRate / compoundingPeriodsPerYear;
        
        // Подготовка массива данных для графика и таблицы
        const yearlyData = [];
        let currentBalance = initialInvestment;
        let totalContributions = initialInvestment; // Начальная сумма уже учтена как вклад
        let totalInterestEarned = 0;
        let totalTaxesPaid = 0;
        
        for (let year = 0; year <= investmentYears; year++) {
          const yearDate = new Date(startDate);
          yearDate.setFullYear(startDate.getFullYear() + year);
          
          const contributionsThisYear = year > 0 && contributionPeriodsPerYear > 0
            ? additionalContribution * contributionPeriodsPerYear
            : 0;
            
          // Добавляем взносы за год к общей сумме взносов
          if (year > 0) { // Для year=0 начальный вклад уже учтен при инициализации
            totalContributions += contributionsThisYear;
          }
          
          // Расчет баланса с учетом сложных процентов
          let balanceBeforeTax = year === 0 
            ? currentBalance 
            : currentBalance;
          
          // Сохраняем баланс в начале года для последующих расчетов
          let balanceAtStartOfYear = currentBalance;
            
          // Для первого года просто показываем начальную сумму
          if (year > 0) {
            // Расчет по формуле сложных процентов с регулярными платежами
            // для разных частот пополнения и капитализации
            
            // Расчет процентов в зависимости от частоты капитализации
            let interestForYear = 0;
            
            if (compoundingPeriodsPerYear === 0) {
              // Без капитализации, проценты начисляются в конце года
              interestForYear = currentBalance * annualInterestRate;
              balanceBeforeTax = currentBalance + interestForYear;
            } else {
              // Расчет с капитализацией и регулярными взносами
              for (let period = 1; period <= compoundingPeriodsPerYear; period++) {
                // Если периоды пополнения и капитализации совпадают, добавляем взнос в начале периода
                const contributionForPeriod = 
                  (contributionPeriodsPerYear > 0 && 
                  period % (compoundingPeriodsPerYear / Math.min(compoundingPeriodsPerYear, contributionPeriodsPerYear)) === 0)
                  ? additionalContribution * (compoundingPeriodsPerYear / contributionPeriodsPerYear)
                  : 0;
                
                // Начисление процентов за период
                const interestForPeriod = currentBalance * compoundingRatePerPeriod;
                interestForYear += interestForPeriod;
                
                // Обновление баланса
                currentBalance += interestForPeriod + contributionForPeriod;
              }
              
              balanceBeforeTax = currentBalance;
            }
            
            // Расчет налогов (НДФЛ) от дохода по процентам
            const taxAmount = interestForYear * taxRate;
            totalTaxesPaid += taxAmount;
            
            // Обновление баланса после налогов
            currentBalance -= taxAmount;
            
            // Обновление общей суммы заработанных процентов
            totalInterestEarned += interestForYear;
          }
          
          // Стоимость с поправкой на инфляцию
          const inflationFactor = Math.pow(1 + inflationRate, year);
          const inflationAdjustedValue = currentBalance / inflationFactor;
          
          yearlyData.push({
            year,
            date: yearDate.toISOString().split('T')[0],
            contributions: year === 0 ? initialInvestment : contributionsThisYear,
            balance: currentBalance,
            interestEarned: year === 0 ? 0 : balanceBeforeTax - balanceAtStartOfYear - contributionsThisYear,
            taxesPaid: year === 0 ? 0 : (balanceBeforeTax - balanceAtStartOfYear - contributionsThisYear) * taxRate,
            inflationAdjustedValue,
            totalContributions: totalContributions,
            totalInterestEarned: totalInterestEarned,
            totalTaxesPaid: totalTaxesPaid
          });
        }
        
        // Расчет финальных результатов
        const finalBalance = currentBalance;
        const finalInflationAdjustedValue = finalBalance / Math.pow(1 + inflationRate, investmentYears);
        const effectiveAnnualRate = Math.pow(finalBalance / initialInvestment, 1 / investmentYears) - 1;
        const effectiveRateInflationAdjusted = Math.pow(finalInflationAdjustedValue / initialInvestment, 1 / investmentYears) - 1;
        
        // Рассчитываем доходность в абсолютных величинах
        const absoluteReturn = finalBalance - totalContributions;
        const absoluteReturnInflationAdjusted = finalInflationAdjustedValue - totalContributions;
        
        // Убедимся, что если абсолютный доход отрицательный, это правильно отражает реальную ситуацию
        // (например, при высокой инфляции или налогах реальная доходность может быть отрицательной)
        const correctedAbsoluteReturn = (absoluteReturn < 0 && totalInterestEarned > 0) 
          ? totalInterestEarned - totalTaxesPaid // альтернативный расчет, если что-то пошло не так
          : absoluteReturn;
        
        // Рассчитываем среднегодовой доход
        const averageAnnualReturn = absoluteReturn / investmentYears;
        
        // Результаты расчета
        const calculationResults = {
          initialInvestment,
          finalBalance,
          finalInflationAdjustedValue,
          totalContributions,
          totalInterestEarned,
          totalTaxesPaid,
          absoluteReturn: correctedAbsoluteReturn, // используем скорректированное значение
          absoluteReturnInflationAdjusted,
          averageAnnualReturn,
          effectiveAnnualRate,
          effectiveRateInflationAdjusted,
          annualInterestRate,
          investmentYears,
          inflationRate,
          taxRate,
          compoundingFrequency,
          contributionFrequency,
          yearlyData
        };
        
        setResults(calculationResults);
        setInvestmentData(yearlyData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setInvestmentData([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setInvestmentData, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      initialInvestment: '100000',
      additionalContribution: '10000',
      contributionFrequency: 'monthly',
      investmentTerm: '10',
      interestRate: '8.5',
      compoundingFrequency: 'annually',
      inflation: '4.0',
      taxRate: '13',
      reinvestDividends: true,
      investmentStartDate: new Date().toISOString().split('T')[0],
      adjustForInflation: true,
      includeAdditionalContributions: true,
    });
    setResults(null);
    setInvestmentData([]);
    setErrors({});
    setSaveInvestmentName('');
    setShowSaveDialog(false);
  };

  // Экспорт данных инвестиции в CSV
  const exportToCSV = () => {
    if (!investmentData || investmentData.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Год', 'Дата', 'Вложения за период', 'Баланс', 'Начисленные проценты', 'Уплаченные налоги', 'С учетом инфляции'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = investmentData.map(item => {
      return [
        item.year,
        item.date,
        formatCsvNumber(item.contributions),
        formatCsvNumber(item.balance),
        formatCsvNumber(item.interestEarned),
        formatCsvNumber(item.taxesPaid),
        formatCsvNumber(item.inflationAdjustedValue)
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.totalContributions),
      formatCsvNumber(results.finalBalance),
      formatCsvNumber(results.totalInterestEarned),
      formatCsvNumber(results.totalTaxesPaid),
      formatCsvNumber(results.finalInflationAdjustedValue)
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Начальная сумма инвестиций:', formatCsvNumber(results.initialInvestment) + ' ₽'],
      ['Срок инвестирования:', `${results.investmentYears} лет`],
      ['Процентная ставка:', `${(results.annualInterestRate * 100).toFixed(2)}%`],
      ['Периодичность капитализации:', getCompoundingFrequencyName(formData.compoundingFrequency)],
      ['Дополнительные взносы:', formData.includeAdditionalContributions ? `${formatCsvNumber(parseFloatOrDefault(formData.additionalContribution))} ₽ (${getContributionFrequencyName(formData.contributionFrequency)})` : 'Нет'],
      ['Инфляция:', formData.adjustForInflation ? `${formData.inflation}%` : 'Не учитывается'],
      ['Налоговая ставка:', `${formData.taxRate}%`],
      ['Дата начала инвестиций:', new Date(formData.investmentStartDate).toLocaleDateString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_инвестиций.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Получение названия периодичности капитализации
  const getCompoundingFrequencyName = (frequency) => {
    const found = compoundingFrequencies.find(f => f.id === frequency);
    return found ? found.name.toLowerCase() : frequency;
  };

  // Получение названия периодичности взносов
  const getContributionFrequencyName = (frequency) => {
    const found = contributionFrequencies.find(f => f.id === frequency);
    return found ? found.name.toLowerCase() : frequency;
  };

  // Обработчик сохранения инвестиции
  const handleSaveInvestment = () => {
    if (showSaveDialog) {
      const savedResult = saveInvestment(saveInvestmentName || null);
      if (savedResult) {
        alert(`Инвестиция "${savedResult.name}" успешно сохранена!`);
      }
      setShowSaveDialog(false);
      setSaveInvestmentName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCompoundInterest();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры инвестиции
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры инвестиции */}
        <InvestmentParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          compoundingFrequencies={compoundingFrequencies}
          investmentTypes={investmentTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Регулярные взносы */}
        <ContributionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          contributionFrequencies={contributionFrequencies}
        />

        {/* Налоги и инфляция */}
        <TaxAndInflationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения инвестиции */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveInvestmentName}
                onChange={(e) => setSaveInvestmentName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveInvestment}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать инвестицию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {investmentData.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать данные (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveInvestment}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;